/**
 * Modern Notification System for InterPals
 * Clean, minimal design inspired by Airbnb/Duolingo
 * Uses vanilla JS - no jQuery dependency
 */

const NotificationConfig = {
  DURATION: {
    ERROR: 5000,
    SUCCESS: 4000,
    WARNING: 5000,
    INFO: 4000
  },
  MAX_NOTIFICATIONS: 3,
  ANIMATION_DURATION: 200,
  // Must clear every app-owned overlay z-index. Third-party chrome, such as the
  // consent manager, stays above. Enforced by
  // scripts/tests/notification-toast-z-index-ceiling.test.js
  Z_INDEX: 1000000,
  // Time before toast ends to trigger follow-up actions (e.g., page reload)
  ACTION_BUFFER: 500
};

const NotificationType = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info'
};

// Expose config globally for other scripts
window.NotificationConfig = NotificationConfig;

class NotificationManager {
  /**
   * Markup a message may keep when its caller passes allowHtml, as tag name to
   * the attributes allowed on it.
   *
   * This mirrors the allowlist Alert::add applies server-side, narrowed to the
   * attributes that are actually used: across all 29 locales the alert
   * translations carry only <a>, <b> and <br>, and only href and target. The
   * server-side strip_tags keeps a tag's attributes wholesale, so the narrowing
   * happens here.
   */
  static ALLOWED_TAGS = {
    A: ['href', 'target'],
    B: [],
    BR: [],
  };

  constructor() {
    this.notifications = [];
    this.container = null;
    this.init();
  }

  init() {
    this.createContainer();
    this.addStyles();
  }

  createContainer() {
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.id = 'notification-container';
    this.container.className = 'fixed inset-x-0 top-0 sm:inset-auto sm:bottom-5 sm:left-5 flex flex-col items-center sm:items-start gap-2 pointer-events-none p-3 sm:p-0';
    this.container.setAttribute('role', 'region');
    this.container.setAttribute('aria-label', 'Notifications');
    this.container.setAttribute('aria-live', 'polite');
    this.container.style.zIndex = NotificationConfig.Z_INDEX;

    document.body.appendChild(this.container);
  }

  addStyles() {
    if (document.getElementById('notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'notification-styles';
    style.textContent = `
      .ip-toast {
        pointer-events: auto;
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
        transition: all ${NotificationConfig.ANIMATION_DURATION}ms cubic-bezier(0.2, 0, 0, 1);
      }

      .ip-toast.show {
        opacity: 1;
        transform: translateY(0) scale(1);
      }

      .ip-toast.hide {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
      }

      @media (min-width: 640px) {
        .ip-toast {
          transform: translateX(-12px) scale(0.98);
        }

        .ip-toast.show {
          transform: translateX(0) scale(1);
        }

        .ip-toast.hide {
          transform: translateX(-12px) scale(0.98);
        }
      }

      .ip-toast-inner a {
        color: white;
        text-decoration: underline;
      }
    `;

    document.head.appendChild(style);
  }

  show(message, type = NotificationType.INFO, options = {}) {
    this.pruneNotifications();

    const notification = this.createNotification(message, type, options);
    this.notifications.push(notification);

    this.container.appendChild(notification.element);

    // Initialize Lucide icons after element is in the DOM
    if (window.LucideIcons) {
      window.LucideIcons.initialize(notification.element);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        notification.element.classList.add('show');
        this.checkMultiline(notification.element);
      });
    });

    if (options.autoHide !== false) {
      const duration = options.duration || NotificationConfig.DURATION[type.toUpperCase()] || 4000;
      notification.timeout = setTimeout(() => this.hide(notification), duration);
    }

    return notification;
  }

  createNotification(message, type, options) {
    const id = 'toast-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
    const config = this.getTypeConfig(type);

    const element = document.createElement('div');
    element.id = id;
    element.className = 'ip-toast w-full max-w-xs sm:max-w-sm';
    element.dataset.type = type;
    element.setAttribute('role', 'alert');

    element.innerHTML = `
      <div class="ip-toast-inner flex items-center gap-3 px-4 py-3 ${config.bg} shadow-lg rounded-full">
        <i data-lucide="${config.icon}" class="flex-shrink-0 w-5 h-5 text-white"></i>
        <p class="flex-1 text-sm font-semibold text-white break-words">${this.renderMessage(message, options)}</p>
        <button type="button" class="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" aria-label="Dismiss">
          <i data-lucide="x" class="w-3.5 h-3.5 text-white"></i>
        </button>
      </div>
    `;

    element.querySelector('button').addEventListener('click', () => this.hide({ id, element }));

    return { id, element, type, message };
  }

  /**
   * Produce the markup for a toast's message.
   *
   * A message is text unless its caller states otherwise. Only the server-rendered
   * alert block sets allowHtml, because alert translations carry markup of their
   * own; every other caller passes a plain string, and a string that happens to
   * contain markup is shown as the characters the user typed.
   */
  renderMessage(message, options) {
    const text = message == null ? '' : String(message);

    return options && options.allowHtml ? this.sanitizeHtml(text) : this.escapeHtml(text);
  }

  /**
   * Reduce markup to the subset alert translations use, dropping the rest.
   *
   * allowHtml states that a message is meant to carry markup; it does not
   * establish that every part of it is safe to render. A translation interpolates
   * runtime values — a username, a URL — so the string reaching this method is
   * only as trustworthy as those values. What survives is therefore fixed here
   * rather than taken on the caller's word: the tags and attributes an audit of
   * all 29 locales found in use, and nothing else. A disallowed tag contributes
   * its text, so a message never loses its wording to sanitization.
   */
  sanitizeHtml(html) {
    // A template's content is inert: parsing it runs no script and fetches no
    // resource, so the tree can be examined before anything of it is rendered.
    const template = document.createElement('template');
    template.innerHTML = html;
    this.scrubNode(template.content);

    return template.innerHTML;
  }

  scrubNode(node) {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        continue;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        continue;
      }

      const allowedAttributes = NotificationManager.ALLOWED_TAGS[child.tagName];
      if (!allowedAttributes) {
        child.replaceWith(...Array.from(child.childNodes));
        continue;
      }

      for (const attribute of Array.from(child.attributes)) {
        const name = attribute.name.toLowerCase();

        if (!allowedAttributes.includes(name)) {
          child.removeAttribute(attribute.name);
          continue;
        }

        if (name === 'href' && !this.isSafeUrl(attribute.value)) {
          child.removeAttribute(attribute.name);
        }
      }

      this.scrubNode(child);
    }
  }

  /**
   * Whether a value is a URL this renderer will keep as an href.
   *
   * Resolved against the document rather than pattern-matched, so the check reads
   * the scheme the browser would act on. That closes the gap between what a regex
   * sees and what a navigation does — an entity-encoded or control-character
   * spelling of a scheme resolves to that scheme here and is rejected on it.
   */
  isSafeUrl(value) {
    // An empty href resolves to the current page, which is a link to nowhere
    // rather than a destination the translation meant. It is dropped so the
    // anchor renders as the plain text it wraps.
    if (!value || value.trim() === '') {
      return false;
    }

    try {
      const url = new URL(value, document.baseURI);

      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  checkMultiline(element) {
    const textEl = element.querySelector('p');
    if (!textEl) return;

    const lineHeight = parseFloat(getComputedStyle(textEl).lineHeight) || 20;
    const height = textEl.getBoundingClientRect().height;

    if (height > lineHeight * 1.5) {
      const inner = element.querySelector('.ip-toast-inner');
      if (!inner) return;
      inner.classList.remove('rounded-full', 'items-center');
      inner.classList.add('rounded-2xl', 'items-start');
    }
  }

  getTypeConfig(type) {
    const configs = {
      success: {
        bg: 'bg-gradient-to-r from-emerald-500 to-teal-500',
        icon: 'check'
      },
      error: {
        bg: 'bg-gradient-to-r from-rose-500 to-pink-500',
        icon: 'triangle-alert'
      },
      warning: {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: 'triangle-alert'
      },
      info: {
        bg: 'bg-gradient-to-r from-sky-500 to-blue-500',
        icon: 'lightbulb'
      }
    };
    return configs[type] || configs.info;
  }

  hide(notification) {
    if (!notification?.element) return;

    if (notification.timeout) clearTimeout(notification.timeout);

    notification.element.classList.remove('show');
    notification.element.classList.add('hide');

    setTimeout(() => {
      notification.element.remove();
      this.notifications = this.notifications.filter(n => n.id !== notification.id);
    }, NotificationConfig.ANIMATION_DURATION);
  }

  pruneNotifications() {
    while (this.notifications.length >= NotificationConfig.MAX_NOTIFICATIONS) {
      const oldest = this.notifications.shift();
      if (oldest) this.hide(oldest);
    }
  }

  clearAll() {
    this.notifications.forEach(n => this.hide(n));
  }
}

let notificationManager;

function initNotificationSystem() {
  if (!notificationManager) {
    notificationManager = new NotificationManager();
  }
  // Built now, not on the first toast: its aria-live only announces content
  // that arrives after the region is already in the tree.
  actionToastContainer();
  return notificationManager;
}

// The ring, call and after-call surfaces own the screen. Only errors show over them.
function callSurfaceActive() {
  try {
    return typeof window.callSurfaceActive === 'function' && window.callSurfaceActive();
  } catch {
    return false;
  }
}

window.showNotification = function(message, type, options) {
  if (type !== NotificationType.ERROR && callSurfaceActive()) return null;
  if (!notificationManager) notificationManager = initNotificationSystem();
  return notificationManager.show(message, type, options);
};

window.showError = (message, options) => window.showNotification(message, NotificationType.ERROR, options);
window.showSuccess = (message, options) => window.showNotification(message, NotificationType.SUCCESS, options);
window.showWarning = (message, options) => window.showNotification(message, NotificationType.WARNING, options);
window.showInfo = (message, options) => window.showNotification(message, NotificationType.INFO, options);

/**
 * Rich push notification with avatar, title, body text, and clickable link.
 * Used by the WebSocket system for new messages, friend requests, etc.
 */
window.showPushNotification = function({ url, username, title, text, img_url }) {
  if (callSurfaceActive()) return null;
  if (!notificationManager) notificationManager = initNotificationSystem();
  notificationManager.pruneNotifications();

  const id = 'toast-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
  const element = document.createElement('div');
  element.id = id;
  element.className = 'ip-toast w-full max-w-xs sm:max-w-sm';
  element.dataset.type = 'push';
  element.setAttribute('role', 'alert');

  const esc = (s) => {
    const d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  };
  const safeTitle = esc(title);
  const safeText = esc(text);
  const avatarUrl = esc(img_url) || '//ipstatic.net/images/male-empty-profile.png';
  const profileUrl = username ? '/' + encodeURIComponent(username) : '#';
  const linkUrl = url ? esc(url) : profileUrl;

  element.innerHTML = `
    <a href="${linkUrl}" class="ip-toast-inner flex items-center gap-3 px-4 py-3 bg-gray-900/95 backdrop-blur shadow-lg rounded-2xl no-underline cursor-pointer group">
      <img src="${avatarUrl}" alt="" class="w-10 h-10 rounded-full object-cover flex-shrink-0">
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-white truncate">${safeTitle}</p>
        ${safeText ? `<p class="text-xs text-gray-300 truncate mt-0.5">${safeText}</p>` : ''}
      </div>
      <button type="button" class="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors ip-toast-close" aria-label="Dismiss">
        <i data-lucide="x" class="w-3.5 h-3.5 text-white"></i>
      </button>
    </a>
  `;

  const notification = { id, element, type: 'push', message: safeTitle };

  element.querySelector('.ip-toast-close').addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    notificationManager.hide(notification);
  });

  notificationManager.notifications.push(notification);
  notificationManager.container.appendChild(element);

  if (window.LucideIcons) {
    window.LucideIcons.initialize(element);
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.classList.add('show');
    });
  });

  notification.timeout = setTimeout(() => notificationManager.hide(notification), 6000);

  return notification;
};

const ACTION_TOAST_DURATION = 5000;

// At most one action toast is live. A deferred commit sits behind the action,
// so a second live toast would mean two pending commitments and one visible way
// back from them.
let actionToast = null;
let actionToastSeq = 0;

/**
 * Bottom-anchored region for action toasts, clear of the mobile bottom nav so
 * the action stays reachable by thumb while its window is open. Separate from
 * the notification container, which sits at the top on small screens.
 */
function actionToastContainer() {
  let container = document.getElementById('notification-action-container');
  if (container) return container;

  container = document.createElement('div');
  container.id = 'notification-action-container';
  container.className = 'fixed inset-x-0 bottom-0 flex flex-col gap-2 px-3 pb-[calc(0.75rem+var(--mobile-bottom-nav-height,0px))] pointer-events-none sm:inset-x-auto sm:left-5 sm:bottom-5 sm:px-0 sm:pb-0';
  // The region announces, not the toast: a live region has to be in the tree
  // before its content changes, and the toast IS the change. No landmark role,
  // because an unnamed region only clutters landmark navigation.
  container.setAttribute('aria-live', 'polite');
  container.style.zIndex = NotificationConfig.Z_INDEX;
  document.body.appendChild(container);

  return container;
}

// Line the region up with the surface the change happened in, so the way back
// sits beside the list it applies to rather than in a corner of the page. Read
// once: the toast outlives neither a resize worth following nor its own window.
function anchorActionToastRegion(container, anchor) {
  container.style.left = '';
  container.style.width = '';

  if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;
  if (window.innerWidth < 640) return;

  const rect = anchor.getBoundingClientRect();
  if (!rect.width) return;

  container.style.left = Math.round(rect.left + 12) + 'px';
  container.style.width = Math.round(Math.max(220, rect.width - 24)) + 'px';
}

/**
 * Toast carrying one action, for a change the viewer can still take back.
 *
 * Showing a second one closes the first, reporting it as unacted-on.
 *
 * @param {string} message           text of the toast
 * @param {Object} [options]
 * @param {string} [options.actionLabel] label of the action button
 * @param {Function} [options.onAction]  run when the action is pressed
 * @param {Function} [options.onClose]   run when the toast goes, true if acted on
 * @param {number} [options.duration]    ms before it closes itself
 * @param {Element} [options.anchor]     surface to line the toast up with
 * @param {boolean} [options.focusAction] put the keyboard on the action button
 * @returns {{dismiss: Function}}
 */
window.showActionToast = function (message, options = {}) {
  if (!notificationManager) notificationManager = initNotificationSystem();

  if (actionToast) actionToast.dismiss();

  const container = actionToastContainer();
  anchorActionToastRegion(container, options.anchor);
  const messageId = `ip-action-toast-message-${++actionToastSeq}`;

  const esc = (s) => {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  };

  const element = document.createElement('div');
  element.className = 'ip-toast pointer-events-auto w-full sm:max-w-sm';
  element.dataset.type = 'action';
  // The inverse surface of mobile ActionToast. Arbitrary values, because
  // dark-overrides.scss repaints the sky utilities under html.dark.
  element.innerHTML = `
    <div class="ip-toast-inner flex min-h-[52px] items-center gap-3 rounded-xl bg-[#2b2f36] py-1.5 pl-4 ${options.actionLabel ? 'pr-1.5' : 'pr-4'} shadow-lg dark:bg-[#ebedf0]">
      <p id="${messageId}" class="min-w-0 flex-1 break-words py-2 text-sm font-medium text-white dark:text-[#1c1f26]">${esc(message)}</p>
      ${options.actionLabel ? `
      <button type="button" aria-describedby="${messageId}" class="ip-action-toast-action inline-flex h-11 min-w-11 flex-none items-center justify-center rounded-lg border-0 bg-transparent px-3 text-sm font-bold text-[#38bdf8] cursor-pointer hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8] dark:text-[#0284c7] dark:hover:bg-black/5 dark:focus-visible:ring-[#0284c7] sm:h-9">${esc(options.actionLabel)}</button>` : ''}
    </div>
  `;

  let settled = false;
  let acted = false;
  let timeout = null;

  const close = (byAction) => {
    if (settled) return;
    settled = true;
    if (byAction) acted = true;
    if (timeout) clearTimeout(timeout);
    if (actionToast && actionToast.element === element) actionToast = null;
    element.classList.remove('show');
    element.classList.add('hide');
    // The action is over. Left clickable through the fade it still looks live,
    // and a press on it does nothing.
    element.classList.remove('pointer-events-auto');
    element.classList.add('pointer-events-none');
    setTimeout(() => element.remove(), NotificationConfig.ANIMATION_DURATION);
    if (typeof options.onClose === 'function') options.onClose(acted);
  };

  const actionBtn = element.querySelector('.ip-action-toast-action');
  if (actionBtn) {
    actionBtn.addEventListener('click', () => {
      if (settled) return;
      // Marked before the callback: an onAction that dismisses the toast
      // itself reaches close() first, and that call would report no action.
      acted = true;
      if (typeof options.onAction === 'function') options.onAction();
      close(true);
    });
  }

  // WCAG 2.2.1: the window is the only way back from the change, so pointing
  // at it or tabbing into it holds it open, and leaving starts it again.
  const hold = () => { if (!settled && timeout) { clearTimeout(timeout); timeout = null; } };
  const resume = () => {
    if (settled || timeout) return;
    timeout = setTimeout(() => close(false), options.duration || ACTION_TOAST_DURATION);
  };
  // Opt-in: the Message Requests undo toast hands Escape to its page listener.
  if (options.closeOnEscape) {
    element.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close(false);
    });
  }
  element.addEventListener('mouseenter', hold);
  element.addEventListener('focusin', hold);
  element.addEventListener('mouseleave', resume);
  element.addEventListener('focusout', resume);

  container.appendChild(element);
  // The toast sits at the end of the body, so a keyboard user cannot tab to it
  // inside its own lifetime. A keyboard-driven action hands them the button.
  if (options.focusAction && actionBtn) actionBtn.focus();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => element.classList.add('show'));
  });

  // Not started when focusAction already put the keyboard on the button: that
  // user is reading the toast, and the window runs from when they leave it.
  if (!element.contains(document.activeElement)) resume();

  actionToast = { element, dismiss: () => close(false) };

  return actionToast;
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initNotificationSystem());
} else {
  initNotificationSystem();
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { NotificationManager, NotificationConfig, NotificationType, initNotificationSystem };
}
